import { ButtonHTMLAttributes, forwardRef } from 'react';
import { LoaderCircle, type LucideIcon } from 'lucide-react';
import { cn } from '../../lib/cn';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'danger-ghost';

const VARIANT_CLASS: Record<ButtonVariant, string> = {
  primary: 'bg-primary text-white hover:bg-primary-dark disabled:bg-primary/50',
  secondary: 'border border-border-strong bg-surface text-text-primary hover:bg-surface-hover disabled:text-text-tertiary',
  ghost: 'text-text-secondary hover:bg-surface-hover hover:text-text-primary disabled:text-text-tertiary',
  danger: 'bg-bad text-white hover:brightness-95 disabled:bg-bad/50',
  'danger-ghost': 'text-bad hover:bg-bad-light disabled:text-text-tertiary',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: 'sm' | 'md';
  icon?: LucideIcon;
  loading?: boolean;
}

/** The app's one button — Tailwind variants instead of MUI's `<Button variant>`. */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', icon: Icon, loading, className, children, disabled, type = 'button', ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={cn(
        'inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-xl font-semibold transition-colors disabled:cursor-not-allowed',
        size === 'sm' ? 'h-8 px-2.5 text-xs' : 'h-9 px-3.5 text-sm',
        VARIANT_CLASS[variant],
        className,
      )}
      {...props}
    >
      {loading ? <LoaderCircle size={size === 'sm' ? 14 : 16} className="animate-spin" /> : Icon && <Icon size={size === 'sm' ? 14 : 16} />}
      {children}
    </button>
  );
});

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: LucideIcon;
  label: string;
  tone?: 'default' | 'danger';
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon: Icon, label, tone = 'default', className, type = 'button', ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      className={cn(
        'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors disabled:cursor-not-allowed disabled:opacity-40',
        tone === 'danger' ? 'text-bad hover:bg-bad-light' : 'text-text-secondary hover:bg-surface-hover hover:text-text-primary',
        className,
      )}
      {...props}
    >
      <Icon size={16} />
    </button>
  );
});
